export interface ScrollPosition {
    top: number,
    left: number,
}

/**
 * Computes potentially adjusted scroll positions honoring scroll-margin-inline/block logical shorthands.
 * Apply logical / physical scroll margin enforcement to the first scroll action produced by
 * compute-scroll-into-view. Returns adjusted top/left values. Handles only the nearest (innermost)
 * scroll ancestor and currently supports LTR for inline adjustments.
 *
 * Algorithm notes:
 * - We read both physical (scrollMarginTop/Left/...) and logical (scroll-margin-inline-start/...) values.
 * - Effective margin for each side is the max between its physical and logical value.
 * - If the visible slack before/after the element is smaller than the required margin, we shift the scroll
 *   so that the margin becomes satisfied, clamping within valid scroll range.
 * - Horizontal logic currently ignores RTL to avoid browser differences in scrollLeft semantics.
 */
export function applyLogicalScrollMargins(
    target: HTMLElement,
    scrollAncestor: Element,
    current: ScrollPosition,
): ScrollPosition {
    let {top, left} = current;

    const style = getComputedStyle(target);

    // Physical margins
    const physicalTop = parseFloat(style.scrollMarginTop || '0') || 0;
    const physicalBottom = parseFloat(style.scrollMarginBottom || '0') || 0;
    const physicalLeft = parseFloat(style.scrollMarginLeft || '0') || 0;
    const physicalRight = parseFloat(style.scrollMarginRight || '0') || 0;

    // Logical margins
    const inlineStartLogical = parseFloat(style.getPropertyValue('scroll-margin-inline-start')) || 0;
    const inlineEndLogical = parseFloat(style.getPropertyValue('scroll-margin-inline-end')) || 0;
    const blockStartLogical = parseFloat(style.getPropertyValue('scroll-margin-block-start')) || 0;
    const blockEndLogical = parseFloat(style.getPropertyValue('scroll-margin-block-end')) || 0;

    const marginInlineStart = Math.max(physicalLeft, inlineStartLogical);
    const marginInlineEnd = Math.max(physicalRight, inlineEndLogical);
    const marginBlockStart = Math.max(physicalTop, blockStartLogical);
    const marginBlockEnd = Math.max(physicalBottom, blockEndLogical);

    if (!(marginInlineStart || marginInlineEnd || marginBlockStart || marginBlockEnd)) {
        return current; // Nothing to enforce.
    }

    const ancestorRect = scrollAncestor.getBoundingClientRect();
    const elRect = target.getBoundingClientRect();

    // Horizontal (LTR only)
    if (ancestorRect.width > 0 && (marginInlineStart || marginInlineEnd)) {
        const isRTL = style.direction === 'rtl';
        if (!isRTL) {
            const diffStart = elRect.left - ancestorRect.left;
            const diffEnd = ancestorRect.right - elRect.right;
            let delta = 0;
            const needLeft = diffStart < marginInlineStart;
            const needRight = diffEnd < marginInlineEnd;
            if (needLeft && !needRight) {
                delta = diffStart - marginInlineStart; // negative
            } else if (needRight && !needLeft) {
                delta = marginInlineEnd - diffEnd; // positive
            } else if (needLeft && needRight) {
                const deficitLeft = marginInlineStart - diffStart;
                const deficitRight = marginInlineEnd - diffEnd;
                delta = deficitLeft >= deficitRight ? (diffStart - marginInlineStart) : (marginInlineEnd - diffEnd);
            }
            if (delta !== 0) {
                let desiredScrollLeft = scrollAncestor.scrollLeft + delta;
                desiredScrollLeft = Math.max(
                    0,
                    Math.min(desiredScrollLeft, scrollAncestor.scrollWidth - ancestorRect.width),
                );
                left = desiredScrollLeft;
            }
        }
    }

    // Vertical
    if (ancestorRect.height > 0 && (marginBlockStart || marginBlockEnd)) {
        const diffStart = elRect.top - ancestorRect.top;
        const diffEnd = ancestorRect.bottom - elRect.bottom;
        let delta = 0;
        const needTop = diffStart < marginBlockStart;
        const needBottom = diffEnd < marginBlockEnd;
        if (needTop && !needBottom) {
            delta = diffStart - marginBlockStart;
        } else if (needBottom && !needTop) {
            delta = marginBlockEnd - diffEnd;
        } else if (needTop && needBottom) {
            const deficitTop = marginBlockStart - diffStart;
            const deficitBottom = marginBlockEnd - diffEnd;
            delta = deficitTop >= deficitBottom ? (diffStart - marginBlockStart) : (marginBlockEnd - diffEnd);
        }
        if (delta !== 0) {
            let desiredScrollTop = scrollAncestor.scrollTop + delta;
            desiredScrollTop = Math.max(
                0,
                Math.min(desiredScrollTop, scrollAncestor.scrollHeight - ancestorRect.height),
            );
            top = desiredScrollTop;
        }
    }

    return {top, left};
}
